// packages/app/src/scaffolder/AdditionalTeamsPicker.tsx

import { useEffect, useState } from 'react';
import { useApi, fetchApiRef, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import CircularProgress from '@material-ui/core/CircularProgress';

interface Team {
  team_key:    string;
  team_name:   string;
  description: string;
}

export const AdditionalTeamsPickerComponent = ({
  onChange,
  rawErrors,
  formData,
  formContext,
  schema,
}: FieldExtensionComponentProps<string[]>) => {
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Read owner selected on Page 1 via formContext
  const selectedOwner = (formContext?.formData?.owner as string) ?? '';

  // Currently selected additional teams
  const selectedTeams: string[] = Array.isArray(formData) ? formData : [];

  useEffect(() => {
    // discoveryApi resolves the real backend URL and the token is attached
    // explicitly — see backendFetch in DashboardPage.tsx for why a plain
    // relative fetchApi.fetch('/api/teams') stops authenticating once
    // frontend and backend are on different origins.
    Promise.all([discoveryApi.getBaseUrl('teams'), identityApi.getCredentials()])
      .then(([baseUrl, { token }]) =>
        fetchApi.fetch(baseUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      )
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch teams: ${res.statusText}`);
        return res.json();
      })
      .then((data: { teams: Team[] }) => {
        setAllTeams(data.teams);
      })
      .catch(() => {
        setError('Could not load teams. Please refresh.');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exclude owner team and plat team always
  const availableTeams = allTeams.filter(
    t => t.team_key !== selectedOwner && t.team_key !== 'plat',
  );

  const handleChange = (teamKey: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedTeams, teamKey]);
    } else {
      onChange(selectedTeams.filter(k => k !== teamKey));
    }
  };

  const hasError = (rawErrors?.length ?? 0) > 0;
  let helperText = 'Optional — select any additional teams that need access to this application';
  if (hasError) {
    helperText = rawErrors![0];
  } else if (selectedOwner) {
    helperText = `Showing all teams except owner (${selectedOwner}) and platform team`;
  }

  if (loading) {
    return (
      <FormControl fullWidth>
        <FormLabel style={{ fontSize: 14, marginBottom: 8 }}>
          {schema?.title ?? 'Additional Teams'}
        </FormLabel>
        <CircularProgress size={20} style={{ marginTop: 8 }} />
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth error>
        <FormLabel style={{ fontSize: 14 }}>
          {schema?.title ?? 'Additional Teams'}
        </FormLabel>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth error={hasError} component="fieldset">
      <FormLabel component="legend" style={{ fontSize: 14, marginBottom: 8 }}>
        {schema?.title ?? 'Additional Teams'}
      </FormLabel>

      {availableTeams.length === 0 ? (
        <FormHelperText>No additional teams available.</FormHelperText>
      ) : (
        <FormGroup>
          {availableTeams.map(team => (
            <FormControlLabel
              key={team.team_key}
              control={
                <Checkbox
                  checked={selectedTeams.includes(team.team_key)}
                  onChange={e => handleChange(team.team_key, e.target.checked)}
                  size="small"
                />
              }
              label={
                <span style={{ fontSize: 13 }}>
                  {team.team_name}
                  <span style={{ fontSize: 11, color: '#888', marginLeft: 6, fontFamily: 'monospace' }}>
                    {team.team_key}
                  </span>
                </span>
              }
            />
          ))}
        </FormGroup>
      )}

      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
